import {
    DndContext,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    rectIntersection,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { useCreateEvent, useDeleteEvent, useUpdateEvent, useUploadFiles } from '@hooks'
import { getErrorMessage } from '@lib/apiError'
import { EventCategory, PutEventType } from '@orbit/api-types'
import { Event } from '@types'
import React from 'react'
import toast from 'react-hot-toast'
import CardContent from '../Card/CardContent'
import Column from '../Column/Column'
import DateSelector from '../DateSelector/DateSelector'
import {
    BoardContainer,
    BoardHeader,
    ColumnsContainer,
    DragOverlayCard,
    EmptyState,
    EmptyStateDescription,
    EmptyStateIcon,
    EmptyStateTitle,
    ScrollableColumnsWrapper,
} from './Board.styles'
import { BoardProps } from './types'

const Board: React.FC<BoardProps> = ({ itinerary, tripId, tripStartDate, tripEndDate, onEditDatesClick, onShareClick, onMapClick, onImportClick }) => {
	const [activeEvent, setActiveEvent] = React.useState<Event | null>(null)
	const createEventMutation = useCreateEvent()
	const updateEventMutation = useUpdateEvent()
	const deleteEventMutation = useDeleteEvent()
	const uploadFilesMutation = useUploadFiles()

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
	)

	const handleDragStart = (dragStartEvent: DragStartEvent) => {
		const activeId = dragStartEvent.active.id as string

		const eventId = activeId.replace('event-', '')

		const sourceDay = itinerary.days.find(day =>
			day.events.some(event => event.id.toString() === eventId),
		)
		const event = sourceDay?.events.find(event => event.id.toString() === eventId)
		setActiveEvent(event || null)
	}

	const handleDragOver = () => {
	}

	const handleDragEnd = async () => {
	}

	const handleAddEvent = async (
		dayId: string,
		title: string,
		description: string,
		category?: EventCategory,
		location?: {
			fullText: string
			primaryText: string
			secondaryText?: string
			googlePlaceId?: string
			googleTypes: string[]
		},
		links?: string[],
		startTime?: string | null,
		createdFromIdeaId?: number,
		pendingFiles?: File[],
	) => {
		const day = itinerary.days.find(d => d.id === dayId)
		if (!day) return

		const tripDayIndex = Number(dayId)

		const filteredLinks = links?.filter(link => link.trim() !== '') || []

		const eventData = {
			name: title,
			description: description || undefined,
			category: category || ('Other' as EventCategory),
			location: location || undefined,
			tripDayIndex,
			tripEventLinks: filteredLinks,
			tags: [],
			...(startTime && { startTime }),
			...(createdFromIdeaId && { createdFromIdeaId }),
			isDeleted: false,
			deletedAt: null,
		}

		try {
			const createdEvent = await createEventMutation.mutateAsync({
				tripId,
				eventData,
			})
			if (pendingFiles && pendingFiles.length > 0) {
				await uploadFilesMutation.mutateAsync({
					tripId,
					files: pendingFiles,
					resourceType: 'TRIP_EVENT',
					resourceId: createdEvent.id,
				})
			}
			toast.success(`'${eventData.name}' added successfully!`)
		} catch (error) {
			console.error('Failed to create event:', error)
			const errorMessage = getErrorMessage(error, 'Failed to add event. Please try again.')
			toast.error(errorMessage)
		}
	}

	const handleUpdateEvent = async (eventId: string, updates: PutEventType) => {
		const event = itinerary.days.flatMap(day => day.events).find(e => e.id.toString() === eventId)

		if (!event) return

		try {
			await updateEventMutation.mutateAsync({
				tripId,
				eventId,
				eventData: updates,
			})
			const eventName = updates.name ?? event.name
			toast.success(`'${eventName}' updated successfully!`)
		} catch (error) {
			console.error('Failed to update event:', error)
			const errorMessage = getErrorMessage(error, 'Failed to update event. Please try again.')
			toast.error(errorMessage)
		}
	}

	const handleDeleteEvent = async (eventId: string) => {
		const event = itinerary.days.flatMap(day => day.events).find(e => e.id.toString() === eventId)
		const eventName = event?.name || 'Event'

		try {
			await deleteEventMutation.mutateAsync({
				tripId,
				eventId,
			})
			toast.success(`'${eventName}' deleted successfully!`)
		} catch (error) {
			console.error('Failed to delete event:', error)
			const errorMessage = getErrorMessage(error, 'Failed to delete event. Please try again.')
			toast.error(errorMessage)
		}
	}

	if (!itinerary.days || itinerary.days.length === 0) {
		return (
			<BoardContainer>
				<EmptyState>
					<EmptyStateIcon>📅</EmptyStateIcon>
					<EmptyStateTitle>No Itinerary Yet</EmptyStateTitle>
					<EmptyStateDescription>
						Select your travel dates to generate your itinerary board
					</EmptyStateDescription>
				</EmptyState>
			</BoardContainer>
		)
	}

	return (
		<BoardContainer>
			<BoardHeader>
				<DateSelector
					initialStartDate={tripStartDate}
					initialEndDate={tripEndDate}
					onEditClick={onEditDatesClick}
					onShareClick={onShareClick}
					onMapClick={onMapClick}
					onImportClick={onImportClick}
				/>
			</BoardHeader>

			<DndContext
				sensors={sensors}
				collisionDetection={rectIntersection}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
			>
				<ScrollableColumnsWrapper>
					<ColumnsContainer id="board-column-container">
						{itinerary.days.map(day => (
							<Column
								key={day.id}
								day={day}
								tripId={tripId}
								onAddEvent={handleAddEvent}
								onUpdateEvent={handleUpdateEvent}
								onDeleteEvent={handleDeleteEvent}
							/>
						))}
					</ColumnsContainer>
				</ScrollableColumnsWrapper>
				<DragOverlay>
					{activeEvent ? (
						<DragOverlayCard>
							<CardContent event={activeEvent} />
						</DragOverlayCard>
					) : null}
				</DragOverlay>
			</DndContext>
		</BoardContainer>
	)
}

export default Board
